import React from 'react';

import HistoryIcon from 'icons/history.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { usePersonnelEventsQuery } from 'components/hooks/personnelEventsQueries';

import Paginator from 'components/application/Paginator';
import Table from 'components/application/Table';
import TableCell from 'components/application/TableCell';
import TextSkeleton from 'components/application/tableSkeletons/TextSkeleton';

import TrainingEventRow from 'components/trainingEvents/TrainingEventRow';

const skeletonRows = Array.from({ length: 25 }, (_, index) => (
  <tr key={`skeleton-${index}`}>
    <TableCell>
      <TextSkeleton />
    </TableCell>
    <TableCell>
      <TextSkeleton />
    </TableCell>
  </tr>
));

export default function TrainingEventsTab({ onPageChange, page, personnelId }) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.TrainingRecords.trainingEventsTab',
  });
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const {
    data: personnelEvents,
    isLoading: isPersonnelEventsLoading,
    isSuccess: isPersonnelEventsFetchSuccess,
  } = usePersonnelEventsQuery({
    params: { page },
    personnelId,
    queryOptions: { meta: { errorMessage: i18nT('fetchError') } },
  });

  const handlePageChange = (event) => {
    onPageChange(Number(event.currentTarget.getAttribute('data-page')));
  };

  const triggererInitialsAndPhoto = (event) => {
    const { profilePhoto: profilePhotos, user: users } =
      personnelEvents.included;

    if (event.attributes.activity.includes('via_api')) {
      return { initials: i18nTShared('api'), userPhotoPath: null };
    }

    const isNadminInitiated = !!event?.relationships?.nadminUser?.data?.id;

    const triggerUser =
      event?.relationships?.triggerer?.data?.id ?
        users[event.relationships.triggerer.data.id]
      : null;

    const initials =
      triggerUser ?
        `${triggerUser.attributes.firstName[0]}${triggerUser.attributes.lastName[0]}`
      : null;

    const triggerUserPhoto =
      triggerUser ?
        Object.values(profilePhotos).find(
          (profilePhoto) =>
            profilePhoto.relationships?.personnel?.data?.id ===
            triggerUser.relationships?.personnel?.data?.id,
        )
      : null;

    const userPhotoPath = triggerUserPhoto?.links?.croppedUrl;

    return { initials, isNadminInitiated, userPhotoPath };
  };

  return (
    (isPersonnelEventsLoading || isPersonnelEventsFetchSuccess) && (
      <>
        <Table
          blankState={
            <div className='tw-rounded-b-md tw-border-1 tw-border-t-0 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-12 tw-text-center'>
              <div className='tw-mx-auto tw-flex tw-h-20 tw-w-20 tw-items-center tw-justify-center tw-rounded-full tw-bg-grey-050'>
                <HistoryIcon
                  className='[&_path]:tw-fill-grey-300'
                  height={42}
                  width={42}
                />
              </div>
              <p className='tw-mb-0 tw-mt-4 tw-text-m tw-text-grey-500'>
                {i18nT('blankState')}
              </p>
            </div>
          }
          headers={
            <tr>
              <th className='tw-w-[30%] tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
                {i18nTShared('date')}
              </th>
              <th className='tw-w-[70%] tw-pl-12 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
                {i18nTShared('change')}
              </th>
            </tr>
          }
          rows={
            isPersonnelEventsLoading ? skeletonRows : (
              personnelEvents.data.map((event) => {
                const { initials, isNadminInitiated, userPhotoPath } =
                  triggererInitialsAndPhoto(event);
                return (
                  <TrainingEventRow
                    isNadminInitiated={isNadminInitiated}
                    key={event.id}
                    personnelEvent={event.attributes}
                    userInitials={initials}
                    userPhotoPath={userPhotoPath}
                  />
                );
              })
            )
          }
          tableClassName={
            isPersonnelEventsFetchSuccess && personnelEvents.data.length === 0 ?
              '!tw-rounded-b-none'
            : ''
          }
        />
        {isPersonnelEventsFetchSuccess &&
          personnelEvents.meta.totalPages > 1 && (
            <div className='tw-mt-20 tw-text-center'>
              <Paginator
                currentPage={personnelEvents.meta.currentPage}
                onClick={handlePageChange}
                totalPages={personnelEvents.meta.totalPages}
              />
            </div>
          )}
      </>
    )
  );
}

TrainingEventsTab.propTypes = {
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  personnelId: PropTypes.string.isRequired,
};
